import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle, Loader2, Share2, Mail, Gift, Globe, BarChart3, Clock, Zap, Play
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function AutoMarketingLibraryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeAutomations, setActiveAutomations] = useState([]);
  const [creatingType, setCreatingType] = useState(null);

  const automationTemplates = [
    {
      type: "social_scheduler",
      name: "Social Media Scheduler",
      description: "Schedule posts across LinkedIn, Twitter, Facebook with one click and optimal timing",
      icon: Share2,
      color: "bg-blue-500",
      confidence: 0.91,
      hoursSaved: 10,
      complexity: "Easy",
      status: "active_by_default"
    },
    {
      type: "email_campaigns",
      name: "Email Campaign Automation",
      description: "Send personalized email campaigns based on user segments and behavior triggers",
      icon: Mail,
      color: "bg-green-500",
      confidence: 0.87,
      hoursSaved: 8,
      complexity: "Easy",
      status: "active_by_default"
    },
    {
      type: "lead_magnet_delivery",
      name: "Lead Magnet Delivery",
      description: "Automatically deliver ebooks, whitepapers, resources when forms are submitted",
      icon: Gift,
      color: "bg-purple-500",
      confidence: 0.83,
      hoursSaved: 6,
      complexity: "Medium",
      status: "optional"
    },
    {
      type: "content_distribution",
      name: "Content Distribution",
      description: "Auto-publish blog content to Medium, LinkedIn, Dev.to and other platforms",
      icon: Globe,
      color: "bg-amber-500",
      confidence: 0.79,
      hoursSaved: 5,
      complexity: "Medium",
      status: "optional"
    },
    {
      type: "analytics_reporting",
      name: "Analytics Reporting",
      description: "Generate and send weekly marketing performance reports with insights automatically",
      icon: BarChart3,
      color: "bg-red-500",
      confidence: 0.85,
      hoursSaved: 4,
      complexity: "Advanced",
      status: "advanced"
    }
  ];

  useEffect(() => {
    fetchActiveAutomations();
  }, []);

  const fetchActiveAutomations = async () => {
    try {
      const response = await axios.get(`${API}/workflows`, { withCredentials: true });
      const automarketing = response.data.filter(w => w.category?.startsWith('automarketing_'));
      setActiveAutomations(automarketing.map(w => w.category));
    } catch (error) {
      console.error('Error fetching automations:', error);
    }
  };

  const handleCreateAutomation = async (type) => {
    setCreatingType(type);
    setLoading(true);
    try {
      await axios.post(
        `${API}/onboarding/automarketing/automation/create-template?automation_type=${type}`,
        {},
        { withCredentials: true }
      );
      
      toast.success("Automation activated!");
      fetchActiveAutomations();
      
    } catch (error) {
      console.error('Error creating automation:', error);
      toast.error(error.response?.data?.detail || "Failed to create automation");
    } finally {
      setLoading(false);
      setCreatingType(null);
    }
  };

  const isAutomationActive = (type) => {
    return activeAutomations.includes(`automarketing_${type.split('_')[0]}`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">AutoMarketing Automation Library</h1>
          <p className="text-muted-foreground mt-2">
            Pre-built automation templates designed for marketing teams and agencies
          </p>
        </div>

        <Card className="glass-card border-primary/30">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-heading font-semibold text-primary mb-1">
                  {automationTemplates.reduce((sum, t) => sum + t.hoursSaved, 0)}h/week
                </div>
                <p className="text-sm text-muted-foreground">Total Time Savings Potential</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-heading font-semibold text-primary mb-1">
                  {activeAutomations.length}/{automationTemplates.length}
                </div>
                <p className="text-sm text-muted-foreground">Active Automations</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-heading font-semibold text-primary mb-1">
                  ${automationTemplates.reduce((sum, t) => sum + t.hoursSaved * 50, 0)}/week
                </div>
                <p className="text-sm text-muted-foreground">Estimated Value</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {automationTemplates.map((template) => {
            const Icon = template.icon;
            const isActive = isAutomationActive(template.type);
            
            return (
              <Card 
                key={template.type} 
                className={`glass-card hover:border-primary/50 transition-all ${
                  isActive ? 'border-green-500/50 bg-green-500/5' : ''
                }`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl ${template.color} flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="font-heading text-lg">{template.name}</CardTitle>
                        {isActive && (
                          <Badge variant="outline" className="mt-1 text-xs bg-green-500/10 text-green-500 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">{template.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{template.hoursSaved}h/week</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Zap className="w-4 h-4" />
                      <span>{Math.round(template.confidence * 100)}%</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>AI Confidence</span>
                      <span>{Math.round(template.confidence * 100)}%</span>
                    </div>
                    <Progress value={template.confidence * 100} className="h-2" />
                  </div>

                  <Button
                    onClick={() => handleCreateAutomation(template.type)}
                    disabled={isActive || loading}
                    className="w-full rounded-full"
                    variant={isActive ? "outline" : "default"}
                  >
                    {loading && creatingType === template.type ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : isActive ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Active
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Activate
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
